import { useForm } from '@tanstack/react-form'
import { ExpenseCreateSchema, ExpenseUpdateSchema } from '../schema/expensesSchema'
import { usePublicUpdateExpenseMutation } from '../hooks/expenses/usePublicUpdateExpenseMutation'
import { ExpenseItemsTable } from './ExpenseItemsTable'
import type {
  ExpenseCategoryData,
  ExpenseData,
  ExpenseItemData,
  ExpenseItemInput,
  ExpenseUpdateData,
} from '../schema/expensesSchema'
import { AutoCompleteSelect } from '@/components/custom/auto-complete-select'
import DatePicker from '@/components/custom/date-picker'
import { FormInput } from '@/components/custom/form-input'
import { FormTextarea } from '@/components/custom/form-textarea'
import LoadingButton from '@/components/custom/loading-button'
import { transformDateToTimezone } from '@/utils/timeZoneUtils'

interface PublicExpenseEditFormProps {
  token: string
  expense: ExpenseData
  categories: ExpenseCategoryData[]
  churches: Array<{ _id: string; name: string }>
  onSuccess: () => void
}

export function PublicExpenseEditForm({
  token,
  expense,
  categories,
  churches,
  onSuccess,
}: PublicExpenseEditFormProps) {
  const { mutateAsync: updateExpense, isPending } = usePublicUpdateExpenseMutation(token, onSuccess)

  const form = useForm({
    defaultValues: {
      _id: expense._id,
      churchId: expense.churchId,
      requester: expense.requester,
      requesterEmail: expense.requesterEmail,
      amount: expense.amount,
      date: new Date(expense.date),
      notes: expense.notes || '',
      expenseItems: expense.expenseItems as Array<ExpenseItemData | ExpenseItemInput>,
    },
    onSubmit: async ({ value }) => {
      const totalAmount = value.expenseItems.reduce((sum, item) => sum + item.amount, 0)
      await updateExpense({
        ...value,
        amount: totalAmount,
        date: transformDateToTimezone(value.date),
      } as ExpenseUpdateData)
    },
  })

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        form.handleSubmit()
      }}
      className="space-y-4"
    >
      <form.Field
        name="requester"
        validators={{
          onBlur: ({ value }) => {
            const result = ExpenseCreateSchema.shape.requester.safeParse(value)
            return result.success ? undefined : result.error.errors[0].message
          },
        }}
        children={(field) => (
          <FormInput field={field} label="Name" id={field.name} name={field.name} required />
        )}
      />

      <form.Field
        name="requesterEmail"
        validators={{
          onBlur: ({ value }) => {
            const result = ExpenseCreateSchema.shape.requesterEmail.safeParse(value)
            return result.success ? undefined : result.error.errors[0].message
          },
        }}
        children={(field) => (
          <FormInput field={field} label="Email" id={field.name} name={field.name} required />
        )}
      />

      <form.Field
        name="churchId"
        validators={{
          onBlur: ({ value }) => {
            const result = ExpenseCreateSchema.shape.churchId.safeParse(value)
            return result.success ? undefined : result.error.errors[0].message
          },
        }}
        children={(field) => (
          <AutoCompleteSelect
            field={field}
            label="Church"
            id={field.name}
            name={field.name}
            options={churches.map((c) => ({ value: c._id, label: c.name }))}
            required
            disabled={isPending}
          />
        )}
      />

      <form.Field
        name="date"
        children={(field) => (
          <DatePicker
            field={field}
            label="Date"
            id={field.name}
            name={field.name}
            startYear={2000}
            disableFutureDates
            required
          />
        )}
      />

      <form.Field
        name="notes"
        validators={{
          onBlur: ({ value }) => {
            const result = ExpenseCreateSchema.shape.notes.safeParse(value)
            return result.success ? undefined : result.error.errors[0].message
          },
        }}
        children={(field) => (
          <FormTextarea
            field={field}
            label="Notes"
            id={field.name}
            name={field.name}
            disabled={isPending}
          />
        )}
      />

      <form.Field
        name="expenseItems"
        validators={{
          onChange: ({ value }) => {
            const result = ExpenseUpdateSchema.shape.expenseItems.safeParse(value)
            return result.success ? undefined : result.error.errors[0].message
          },
        }}
        children={(field) => (
          <div>
            <ExpenseItemsTable
              items={field.state.value}
              categories={categories}
              disabled={isPending}
              onChange={(items) => field.handleChange(items)}
            />
            {field.state.meta.errors.length > 0 && (
              <p className="text-sm text-destructive mt-2">{field.state.meta.errors[0]}</p>
            )}
          </div>
        )}
      />

      <form.Subscribe
        selector={(state) => [state.canSubmit]}
        children={([canSubmit]) => (
          <LoadingButton
            type="submit"
            className="w-full mt-4"
            isLoading={isPending}
            disabled={!canSubmit || isPending}
          >
            {isPending ? 'Submitting...' : 'Submit'}
          </LoadingButton>
        )}
      />
    </form>
  )
}
